import {AfterViewInit, Component, ElementRef, ViewChild} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {PaymentType} from '../layouts/payment-type/payment-type';
import {AccountType} from '../enums/account-type';
import {SubscriptionType} from '../enums/subscription-type';
import {Language} from '../models/language';
import {InitPaymentRequest} from "../models/payment/initPaymentRequest";
import {InitPaymentResponse} from "../models/payment/initPaymentResponse";
import {PromoCodeService} from "../service/promo-code.service";
import {FormControl} from "@angular/forms";
import {DiscountRequest} from "../models/discountRequest";
import {DiscountResponse} from "../interface/discountResponse";
import {HttpErrorResponse} from "@angular/common/http";
import {SubscriptionPlanInfoService} from "../service/subscription-plan-info.service";
import {SubscriptionPlanInfoResponse} from "../interface/subscriptionPlanInfoResponse";
import {SubscriptionInfoPaymentModel} from "./subscriptionInfoPaymentModel";
import {BehaviorSubject} from "rxjs";
import {MockupDataModel} from "../qr-copy-message/mockup-data.model";
import {SnackBarUtil} from "../qr-copy-message/snack-bar-util.service";
import {
  INVALID_PROMO_CODE_MESSAGE,
  PROMO_ALREADY_USED_ERROR_MESSAGE, PROMO_CODE_PATTERN,
  PROMO_LIMIT_EXPIRATION_ERROR_MESSAGE,
  SUCCESS_PROMO_APPLY_MESSAGE
} from "../constants/promo-code.constant";
import {PromoCodeUtil} from "../service/util/promo-code-util.service";
import {DOLLAR_TO_DRAM_CURRENCY} from "../constants/currency.constant";
import {SubscriptionPaymentService} from "./subscription-payment.service";
import {PaymentBackUrlParamsHandlerService} from "./payment-back-url-params-handler.service";
import {SidebarComponent} from "../sidebar/sidebar.component";
import {CreditCardService} from "../service/credit-card.service";
import {CreditCardResponse} from "../models/creditCardResponse";
import {MakeBindingPaymentResponse} from "../models/payment/makeBindingPaymentResponse";
import {TransactionOpaque} from "../models/payment/opaque/transactionOpaque";
import {AMERIA_FORM, PAYMENT_ID_NAME, SUCCESS_AMERIA_RESPONSE_CODE} from "../constants/payment.constant";
import {SubscriptionBuyingService} from "../service/subscription-buying.service";
import {UserService} from "../service/user.service";
import {UserResponse} from "../interface/userResponse";
import {TranslateService} from "@ngx-translate/core";

@Component({
  selector: 'app-subscription-payment',
  templateUrl: './subscription-payment.component.html',
  styleUrls: ['./subscription-payment.component.scss', '../subscription-plan/subscription-plan-card/subscription-plan-card.component.scss']
})
export class SubscriptionPaymentComponent implements AfterViewInit {

  protected readonly DOLLAR_TO_DRAM_CURRENCY = DOLLAR_TO_DRAM_CURRENCY;
  protected subscriptionInfoPaymentModel!: SubscriptionInfoPaymentModel;

  protected paymentTypeSelected: PaymentType = PaymentType.CREDIT_CARD;
  protected currentLang: string = '';

  protected isMobileDropdownOpened = false;

  protected readonly FREE: string = 'subscription_plan_content.free';
  protected readonly MONTHLY: string = 'subscription_plan_content.monthly';
  protected readonly YEARLY: string = 'subscription_plan_content.yearly';

  protected promoCodeFormControl: FormControl<string | null> = new FormControl<string>('');

  protected isPromoCodeEmpty: boolean = false;
  protected isPromoCodeLengthInvalid: boolean = false;
  protected isPromoPatternInvalid: boolean = false;
  protected isPromoCodeResponseInvalid: boolean = false;
  protected isPromoCodeExpired: boolean = false;
  protected isSubscriptionMonthly: boolean = true;

  protected isPromoCodeActivated: boolean = false;
  protected isPromoCodeInvalid: boolean = false;
  protected newPrice: number | null = null;

  protected creditCards: Array<CreditCardResponse> = [];
  protected selectedCreditCard: CreditCardResponse | null | undefined;
  protected isPayClicked: boolean = false;

  protected currentUser?: UserResponse;

  public promoErrorMessage: string | null = null;
  public static onClickPayNowFromCommon: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  @ViewChild("spDropdown") spDropdownRef!: ElementRef<HTMLDivElement>;

  constructor(
    private activatedRoute: ActivatedRoute,
    private subscriptionBuyingService: SubscriptionBuyingService,
    private promoCodeService: PromoCodeService,
    private subscriptionInfoService: SubscriptionPlanInfoService,
    private snackBar: SnackBarUtil,
    private subscriptionPaymentService: SubscriptionPaymentService,
    private router: Router,
    private creditCardService: CreditCardService,
    private userService: UserService,
    private translate: TranslateService
  ) {
    const subscriptionId = Number(this.activatedRoute.snapshot.paramMap.get('id'));
    const isMonthlyString = this.activatedRoute.snapshot.paramMap.get('isMonthly');
    const isMonthly = isMonthlyString == 'true';
    this.isSubscriptionMonthly = isMonthly;
    this.subscriptionInfoService.getById(subscriptionId).subscribe({
      next: (response: SubscriptionPlanInfoResponse) => {
        this.subscriptionInfoPaymentModel = this.prepareInfoResponse(response, isMonthly);
      }
    });

    SubscriptionPaymentComponent.onClickPayNowFromCommon.subscribe((value: boolean) => {
      if (value) {
        this.pay();
      }
    })

    this.userService.currentUser.subscribe({
      next: (currentUser: UserResponse) => {
        this.currentUser = currentUser;
      }
    })
  }

  ngOnInit() {
    this.creditCardService.getAll().subscribe({
      next: (creditCard: Array<CreditCardResponse>) => {
        this.creditCards = creditCard;
      }
    })
  }

  ngAfterViewInit() {
    this.handleSpDropdown(this.spDropdownRef.nativeElement);
  }

  get getPromoCodeErrorMessage() {
    if (this.isPromoCodeEmpty){
      return this.translate.instant('promo_code_mes.promo_code_is_required');
    } else if (this.isPromoPatternInvalid) {
      return this.translate.instant('promo_code_mes.promo_code_pattern_invalid');
    } else if (this.isPromoCodeResponseInvalid) {
      return this.translate.instant('promo_code_mes.promo_code_invalid');
    } else if (this.isPromoCodeExpired) {
      return this.translate.instant('promo_code_mes.promo_code_expired');
    }
    return "";
  }

  protected pay() {
    if(this.isPayClicked) return;
    this.isPayClicked = true;
    const promoCode: string | null = this.promoCodeFormControl.getRawValue()


    const opaque: string = JSON.stringify({
      subscriptionInfoId: this.subscriptionInfoPaymentModel.subscriptionPlanData.id,
      isMonthly: !this.subscriptionInfoPaymentModel.isYearly,
      promoCode: promoCode,
      currentUserId: this.currentUser?.id
    });

    if (this.newPrice != 0) {
      const actualPrice = this.subscriptionInfoPaymentModel.subscriptionPlanData.price;
      const request: InitPaymentRequest = InitPaymentRequest.initForSubscriptionBuying("Subscription buying", actualPrice, opaque, this.subscriptionInfoPaymentModel.isIndividuals, !this.subscriptionInfoPaymentModel.isYearly, promoCode);
      console.debug('[SubscriptionPayment] init payment request', {
        amount: request.amount,
        isMonthly: request.isMonthly,
        subscriptionInfoId: this.subscriptionInfoPaymentModel.subscriptionPlanData.id,
        promoCode: request.promoCode
      });

      if(this.paymentTypeSelected == PaymentType.CREDIT_CARD) {
        this.buyWithNewCard(request);
      } else if (this.paymentTypeSelected == PaymentType.EXISTING_CREDIT_CARD) {
        this.buyWithExistingCreditCard(request, opaque);
      }
    } else {
      this.buyingSubscriptionWith100PercentPromoCode(promoCode);
    }
  }

  private buyWithExistingCreditCard(request: InitPaymentRequest, opaque: string) {
    if(!this.selectedCreditCard) return;
    request.cardHolderID = this.selectedCreditCard.cardHolderId;
    request.payWithNewCard = false;
    this.subscriptionBuyingService.makeBindingPayment(request, TransactionOpaque.withSubscriptionBuyingOpaque(JSON.parse(opaque)))
      .subscribe({
        next: (paymentResponse: MakeBindingPaymentResponse) => {
          const isSuccess = paymentResponse.responseCode == SUCCESS_AMERIA_RESPONSE_CODE;
          if (isSuccess) {
            this.router.navigate(['/cards'], {state: {successPayment: true}}).then();
          } else {
            const data = MockupDataModel.error("There was an issue processing your payment.");
            this.showSnackbar(data, SnackBarUtil.CLASSES.error);
          }
        },
        error: (error: HttpErrorResponse) => {
          this.handlePurchaseError(error);
          this.isPayClicked = false;
        },
        complete: () => {
          this.isPayClicked = false;
        }
      });
  }

  private buyWithNewCard(request: InitPaymentRequest) {
    request.payWithNewCard = true;
    this.subscriptionBuyingService.init(request)
      .subscribe({
        next: (paymentResponse: InitPaymentResponse) => {
          window.location.href = AMERIA_FORM.replace(PAYMENT_ID_NAME, paymentResponse.PaymentID);
        },
        error: (error: HttpErrorResponse) => {
          this.handlePurchaseError(error);
          this.isPayClicked = false;
        },
        complete: () => {
          this.isPayClicked = false;
        }
      });
  }

  private buyingSubscriptionWith100PercentPromoCode(promoCode: string | null) {
    const subscriptionInfoId = this.subscriptionInfoPaymentModel.subscriptionPlanData.id;
    this.subscriptionPaymentService.purchase(subscriptionInfoId, !this.subscriptionInfoPaymentModel.isYearly, promoCode)
      .subscribe({
        next: () => {
          const data = MockupDataModel.success(SUCCESS_PROMO_APPLY_MESSAGE);
          PaymentBackUrlParamsHandlerService.paymentSuccess.next();
          SidebarComponent.initializer.next(true);
          this.showSnackbar(data, SnackBarUtil.CLASSES.success);
          this.router.navigate(['cards']).then();
        },
        error: (error: HttpErrorResponse) => {
          this.handlePurchaseError(error);
          this.isPayClicked = false;
        },
        complete: () => {
          this.isPayClicked = false;
        }
      })
  }

  protected selectType(type: PaymentType, creditCard?: CreditCardResponse) {
    if (creditCard) {
      this.selectedCreditCard = creditCard;
    } else {
      this.selectedCreditCard = null;
    }
    this.paymentTypeSelected = type;
  }

  protected handleSpDropdown(dropdownEl: HTMLDivElement) {
    if (!this.isMobileDropdownOpened) {
      dropdownEl.classList.add('expanded');
    } else {
      dropdownEl.classList.remove('expanded');
    }
    this.isMobileDropdownOpened = !this.isMobileDropdownOpened;
  }

  protected applyPromoCode() {
    if (this.checkIsPromoCodeInvalid()) {
      this.isPromoCodeInvalid = true;
      return
    }

    this.isPromoCodeInvalid = false;
    const promoCode = this.promoCodeFormControl.getRawValue();

    const discountRequest = new DiscountRequest(promoCode, this.subscriptionInfoPaymentModel.subscriptionPlanData.id);
    this.promoCodeService.discount(discountRequest).subscribe({
      next: (discountResponse: DiscountResponse) => {
        this.isPromoCodeActivated = true;
        this.newPrice = this.roundPrice(discountResponse.price);
        const message = this.translate.instant('promo_code_messages.success_message');
        this.showSnackbar(MockupDataModel.success(message), SnackBarUtil.CLASSES.success);
      },
      error: (error: HttpErrorResponse) => {
        if (PromoCodeUtil.isPromoCodeNotFound(error)) {
          this.isPromoCodeResponseInvalid = true;
          this.isPromoCodeInvalid = true;
        } else if (PromoCodeUtil.isPromoCodeLimitExpired(error)) {
          this.showSnackbar(MockupDataModel.error(PROMO_LIMIT_EXPIRATION_ERROR_MESSAGE), SnackBarUtil.CLASSES.error);
        } else if (PromoCodeUtil.isPromoCodeAlreadyUsed(error)) {
          this.showSnackbar(MockupDataModel.error(PROMO_ALREADY_USED_ERROR_MESSAGE), SnackBarUtil.CLASSES.error);
        } else if (PromoCodeUtil.isPromoCodeExpired(error)) {
          this.isPromoCodeExpired = true;
          this.isPromoCodeInvalid = true;
        } else if (PromoCodeUtil.isPromoCodeInvalid(error)) {
          this.showSnackbar(MockupDataModel.error(this.translate.instant(INVALID_PROMO_CODE_MESSAGE)), SnackBarUtil.CLASSES.error);
        }
      }
    });
  }

  protected roundNumber(number: number) {
    return Math.round(number * 100) / 100;
  }

  protected onPromoCodeInput() {
    this.isPromoCodeInvalid = false;
    this.isPromoCodeResponseInvalid = false;
    this.isPromoPatternInvalid = false;
    this.isPromoCodeLengthInvalid = false;
    this.isPromoCodeExpired = false;
  }

  protected roundPrice(price: number): number {
    return Math.round(price * 1000) / 1000;
  }

  private checkIsPromoCodeInvalid() {
    const promoRawValue = this.promoCodeFormControl.getRawValue();
    const promoCode = promoRawValue ? promoRawValue : '';

    if (!promoCode) {
      this.isPromoCodeEmpty = true;
      this.isPromoCodeLengthInvalid = false;
      this.isPromoPatternInvalid = false;
      this.promoErrorMessage = this.translate.instant('PROMO_CODE.PROMO_CODE_IS_REQUIRED');
      return false;
    }
    this.isPromoCodeEmpty = false;

    if ((!this.promoCodeFormControl.touched && !this.promoCodeFormControl.dirty) || promoCode == '') return;

    if (!promoCode.match(PROMO_CODE_PATTERN)) {
      this.isPromoCodeLengthInvalid = false;
      this.isPromoPatternInvalid = true;
      return true;
    }
    return false;
  }

  private prepareInfoResponse(response: SubscriptionPlanInfoResponse, isMonthly: boolean) {
    const isIndividual = response.parent.accountType == AccountType.INDIVIDUAL;
    const isStarter = response.subscriptionType == SubscriptionType.STARTER;
    const isStandard = response.subscriptionType == SubscriptionType.STANDARD;
    const isYearly = !isMonthly;
    const startDate = new Date();
    const endDate = this.plus30Days(startDate);
    return new SubscriptionInfoPaymentModel(
      response,
      isIndividual,
      isStarter,
      isStandard,
      isYearly,
      startDate,
      endDate
    );
  }

  private plus30Days(date: Date) {
    return new Date(new Date().setMonth(date.getMonth() + 1))
  }

  private handlePurchaseError(error: HttpErrorResponse): void {
    if (error.status === 409) {
      this.showSnackbar(
        MockupDataModel.error(this.translate.instant('current_subscription_exists_message')),
        SnackBarUtil.CLASSES.error
      );
    } else if (error.status === 402) {
      this.showSnackbar(
        MockupDataModel.error(this.translate.instant('insufficient_funds_message')),
        SnackBarUtil.CLASSES.error
      );
    } else {
      this.showErrorSnackbar();
    }
  }

  private showErrorSnackbar() {
    const data = MockupDataModel.error(
      this.translate.instant('error_payment_transaction_message')
    );
    this.showSnackbar(data, SnackBarUtil.CLASSES.error);
  }

  private showSnackbar(data: MockupDataModel, panelClassNames: string[]): void {
    this.snackBar.openSnackBar({
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: panelClassNames,
      data: data,
    });
  }

  protected readonly PaymentType = PaymentType;
  protected readonly Language = Language;

  getTranslatedSubscriptionTitle(subscriptionType: SubscriptionType): string {
    const translationKey = `subscription_plans.${subscriptionType.toLowerCase()}`;
    return this.translate.instant(translationKey);
  }
}
